import {
  APIProvider,
  InfoWindow,
  Map,
  useMap,
} from '@vis.gl/react-google-maps';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  convertStatus,
  convertStatusUrl,
  Status,
} from '../../../../api/enumerations';
import { WorkOrderData } from '../../../../api/workOrders/types';
import {
  SLA_COLORS,
  SLA_TONE_LABEL,
  slaTone,
} from '../../../../constants/home';
import { STATUS_COLORS } from '../../../../constants/status';
import {
  Banner,
  ColorByToggle,
  Legend,
  LegendDot,
  LegendRow,
  MapPane,
  MapWrapper,
  Popover,
  Sidebar,
  SidebarHeader,
  SidebarItem,
  SidebarList,
} from './styles';
import { useClusteredMarkers } from './useClusteredMarkers';

interface MapViewProps {
  data: WorkOrderData[];
  loading: boolean;
  onSwitchToTable?: () => void;
}

type ColorBy = 'sla' | 'status';

const colorByStrategy = (mode: ColorBy): ((item: WorkOrderData) => string) =>
  mode === 'sla'
    ? (item) => SLA_COLORS[slaTone(item.sla_status)]
    : (item) => STATUS_COLORS[item.status] || '#9E9E9E';

const hasValidCoords = (item: WorkOrderData): boolean => {
  const lat = Number(item.latitude);
  const lng = Number(item.longitude);
  return !Number.isNaN(lat) && !Number.isNaN(lng) && lat !== 0 && lng !== 0;
};

const BRAZIL_CENTER = { lat: -14.235, lng: -51.925 };

interface MapContentProps {
  itemsWithCoords: WorkOrderData[];
  colorBy: ColorBy;
  selected: WorkOrderData | null;
  onSelect: (item: WorkOrderData | null) => void;
  onVisibleChange: (ids: number[]) => void;
}

function MapContent({
  itemsWithCoords,
  colorBy,
  selected,
  onSelect,
  onVisibleChange,
}: MapContentProps): JSX.Element | null {
  const map = useMap();
  const colorOf = useMemo(() => colorByStrategy(colorBy), [colorBy]);

  useClusteredMarkers({
    items: itemsWithCoords,
    colorOf,
    onSelect,
    onBoundsIds: onVisibleChange,
  });

  if (!selected) return null;

  return (
    <InfoWindow
      position={{
        lat: Number(selected.latitude),
        lng: Number(selected.longitude),
      }}
      onCloseClick={() => onSelect(null)}
    >
      <Popover>
        <strong>OS {selected.reference_number}</strong>
        <span>{selected.client_name || '—'}</span>
        <span>
          {convertStatus(selected.status)}
          {selected.worker_company?.name
            ? ` • ${selected.worker_company.name}`
            : ''}
        </span>
        <Link
          to={`/home/property/${selected.id}/${convertStatusUrl(
            selected.status
          )}`}
          onClick={() => {
            if (map) onSelect(null);
          }}
        >
          ver detalhes →
        </Link>
      </Popover>
    </InfoWindow>
  );
}

export function MapView({
  data,
  loading,
  onSwitchToTable,
}: MapViewProps): JSX.Element {
  const [colorBy, setColorBy] = useState<ColorBy>('sla');
  const [selected, setSelected] = useState<WorkOrderData | null>(null);
  const [visibleIds, setVisibleIds] = useState<number[]>([]);
  const [authFailed, setAuthFailed] = useState(false);

  useEffect(() => {
    type GoogleAuthWindow = Window & { gm_authFailure?: () => void };
    const w = window as GoogleAuthWindow;
    const previous = w.gm_authFailure;
    w.gm_authFailure = () => setAuthFailed(true);
    return () => {
      w.gm_authFailure = previous;
    };
  }, []);

  const itemsWithCoords = useMemo(() => data.filter(hasValidCoords), [data]);
  const itemsWithoutCoords = data.length - itemsWithCoords.length;

  const handleVisibleChange = useCallback((ids: number[]) => {
    setVisibleIds(ids);
  }, []);

  const visibleItems = useMemo(() => {
    if (visibleIds.length === 0) return itemsWithCoords;
    const set = new Set(visibleIds);
    return itemsWithCoords.filter((it) => set.has(it.id));
  }, [visibleIds, itemsWithCoords]);

  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

  if (!apiKey) {
    return (
      <MapWrapper>
        <MapPane>
          <Banner>chave do Google Maps não configurada</Banner>
        </MapPane>
      </MapWrapper>
    );
  }

  return (
    <MapWrapper>
      <MapPane>
        <APIProvider apiKey={apiKey} libraries={['marker']}>
          <Map
            className="customMap"
            defaultCenter={BRAZIL_CENTER}
            defaultZoom={4}
            gestureHandling="greedy"
            disableDefaultUI={false}
            streetViewControl={false}
            fullscreenControl={false}
            mapTypeControl={false}
          >
            <MapContent
              itemsWithCoords={itemsWithCoords}
              colorBy={colorBy}
              selected={selected}
              onSelect={setSelected}
              onVisibleChange={handleVisibleChange}
            />
          </Map>
        </APIProvider>
        {authFailed && (
          <Banner
            style={{
              top: '12px',
              backgroundColor: 'rgba(253, 236, 236, 0.97)',
              color: '#B71C1C',
            }}
          >
            <span>
              chave do Google Maps não autorizada para{' '}
              <strong>{window.location.host}</strong> — adicione este domínio às
              restrições da API key no Google Cloud Console
            </span>
          </Banner>
        )}
        {itemsWithoutCoords > 0 && (
          <Banner style={authFailed ? { top: '60px' } : undefined}>
            <span>
              {itemsWithoutCoords} OS sem coordenadas — não aparecem no mapa
            </span>
            {onSwitchToTable && (
              <ColorByToggle onClick={onSwitchToTable} variant="outlined">
                ver na tabela
              </ColorByToggle>
            )}
          </Banner>
        )}
        <Legend>
          <strong style={{ fontSize: 12 }}>
            colorir por:{' '}
            <ColorByToggle
              onClick={() => setColorBy(colorBy === 'sla' ? 'status' : 'sla')}
              variant="outlined"
              size="small"
            >
              {colorBy === 'sla' ? 'SLA' : 'Status'}
            </ColorByToggle>
          </strong>
          {colorBy === 'sla' && (
            <>
              <LegendRow>
                <LegendDot color={SLA_COLORS.ok} /> no prazo
              </LegendRow>
              <LegendRow>
                <LegendDot color={SLA_COLORS.near} /> próximo do prazo
              </LegendRow>
              <LegendRow>
                <LegendDot color={SLA_COLORS.overdue} /> atrasada
              </LegendRow>
              <LegendRow>
                <LegendDot color={SLA_COLORS.unknown} /> sem SLA
              </LegendRow>
            </>
          )}
          {colorBy === 'status' && (
            <>
              <LegendRow>
                <LegendDot color={STATUS_COLORS[Status.ENTRANCE]} /> entrada
              </LegendRow>
              <LegendRow>
                <LegendDot color={STATUS_COLORS[Status.SCHEDULE]} /> agendamento
              </LegendRow>
              <LegendRow>
                <LegendDot color={STATUS_COLORS[Status.INSPECTION]} /> em
                vistoria
              </LegendRow>
              <LegendRow>
                <LegendDot color={STATUS_COLORS[Status.REVISION]} /> revisão
              </LegendRow>
            </>
          )}
        </Legend>
      </MapPane>
      <Sidebar>
        <SidebarHeader>
          <span>
            {loading ? 'carregando...' : `${visibleItems.length} OS visíveis`}
          </span>
        </SidebarHeader>
        <SidebarList>
          {visibleItems.slice(0, 50).map((it) => {
            const tone = slaTone(it.sla_status);
            return (
              <SidebarItem
                key={it.id}
                onClick={() => setSelected(it)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setSelected(it);
                  }
                }}
                role="button"
                tabIndex={0}
                aria-label={`OS ${it.reference_number}, ${
                  it.client_name || 'sem cliente'
                }`}
              >
                <strong>
                  OS {it.reference_number} •{' '}
                  <span style={{ color: SLA_COLORS[tone] }}>
                    {SLA_TONE_LABEL[tone]}
                  </span>
                </strong>
                <span>{it.client_name || '—'}</span>
                <span className="meta">
                  {it.worker_company?.name || 'sem empresa'} •{' '}
                  {convertStatus(it.status)}
                </span>
              </SidebarItem>
            );
          })}
          {visibleItems.length === 0 && !loading && (
            <SidebarItem>nenhuma OS no viewport</SidebarItem>
          )}
        </SidebarList>
      </Sidebar>
    </MapWrapper>
  );
}
